import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { skipWhile, switchMap, take } from 'rxjs/operators';

import { selectActiveSite } from 'src/app/store/site/site.selectors';

import { PaymentService, PaymentSitesResponse } from '../../../core/api/payment.service';
import { SiteService } from '../../../core/api/site.service';
import { MapService } from '../../../core/helper/map.service';
import { RedirectService } from '../../../core/helper/redirect.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { DialogChangeSiteStateComponent } from '../../../shared/components/dialog-change-site-state/dialog-change-site-state.component';
import { RoleEnum } from '../../../shared/constants/role.enums';
import { StatusEnum } from '../../../shared/constants/site_status.enum';
import { DialogConfirmPaymentComponent } from '../../../shared/dialogs/dialog-confirm-payment/dialog-confirm-payment.component';
import { DialogSendEdocComponent } from '../../../shared/dialogs/dialog-send-edoc/dialog-send-edoc.component';
import { DialogSplitSiteComponent } from '../../../shared/dialogs/dialog-split-site/dialog-split-site.component';
import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Site, User } from '../../../shared/models';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import * as fromApp from '../../../store';
import { selectCurrentUser } from '../../../store/auth/auth.selectors';
import { getDocuments } from '../../../store/document/document.actions';
import { setMapControlDrawMode } from '../../../store/map-control/map-control.actions';
import { clearActiveSite, setActiveSite } from '../../../store/site/site.actions';
import { DialogSitePaymentResultsComponent } from '../dialog-site-payment-results/dialog-site-payment-results.component';
import { StatusCategoryEnum } from "../../../shared/constants/site_status_category.enum";

@Component({
  selector: 'fc-admin-site-detail-page',
  templateUrl: './admin-site-detail-page.component.html',
  styleUrls: ['./admin-site-detail-page.component.scss'],
})
export class AdminSiteDetailPageComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  id: number = null;
  site: Site = null;
  allowPayment: boolean;
  hasStripe: boolean = false;
  isSuperAdmin = false;
  canPaySites = false;
  isLimitedAdmin = false;
  canChangeStatus = false;
  isAdmin = false;
  step1Expanded: boolean;
  step2Expanded: boolean;
  step3Expanded: boolean;
  currentUser: User;

  constructor(
    readonly route: ActivatedRoute,
    readonly router: Router,
    public dialog: MatDialog,
    readonly paymentService: PaymentService,
    readonly siteHelper: SiteHelperService,
    readonly siteService: SiteService,
    readonly spinnerService: SpinnerService,
    readonly redirectService: RedirectService,
    readonly mapHelper: MapService,
    readonly store: Store<fromApp.AppState>
  ) {}

  ngOnInit() {
    this.id = +this.route.snapshot.params.id;
    this.store.dispatch(clearActiveSite());
    this.store.dispatch(setActiveSite({ id: this.id }));
    this.store
      .select(selectActiveSite)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skipWhile(site => site === null)
      )
      .subscribe(activeSite => {
        this.site = activeSite;
        this.allowPayment = this.siteHelper.sitesArePayable([activeSite]);
        this.store.dispatch(getDocuments({ identifier: this.site.identifier }));
        this.setExpansionPanels();
      });

    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((currentUser: User) => {
        this.isSuperAdmin = currentUser.isSuperAdmin;
        this.canPaySites = currentUser.canPaySites;
        this.isLimitedAdmin = currentUser.isLimitedAdmin;
        this.canChangeStatus = !currentUser.hasRole(RoleEnum.FinanceAdmin);
        this.isAdmin = currentUser.isAdmin;
        this.currentUser = currentUser;
      });

    this.store
      .select(selectActiveSite)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skipWhile(site => site === null)
      )
      .subscribe({
        next: site => {
          this.site = site;
          this.hasStripe = this.site.account.user.hasStripe(this.site.cohort.buyerId);
        },
      });
  }

  setExpansionPanels(): void {
    if (this.site.approvedDate === null) {
      this.step1Expanded = true;
      this.step2Expanded = false;
      this.step3Expanded = false;
    } else if (this.site.status === StatusEnum.APPROVED || this.site.status === StatusEnum.APPROVED_CONDITIONALLY) {
      this.step1Expanded = false;
      this.step2Expanded = true;
      this.step3Expanded = false;
    } else {
      this.step1Expanded = false;
      this.step2Expanded = false;
      this.step3Expanded = true;
    }
  }

  doChangeSiteState(transitionToState: StatusEnum): void {
    this.siteService
      .changeSitesState([this.site.id], this.site.status, transitionToState)
      .pipe(
        switchMap(response => {
          if (response.error) {
            throw new Error('ERROR! ' + response.error);
          } else {
            return this.siteService.getSite(this.id);
          }
        })
      )
      .subscribe(
        site => {
          this.site = site;
          this.setExpansionPanels();
          this.allowPayment = this.siteHelper.sitesArePayable([site]);
        },
        error => console.log(error)
      );
  }

  onBulkChangeSiteState(): void {
    const dialogRef = this.dialog.open(DialogChangeSiteStateComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        categories: this.isLimitedAdmin ? [StatusCategoryEnum.APPROVED] : this.siteHelper.allCategoryValues,
        sites: [this.site],
        buttonTextYes: 'Confirm',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.store.dispatch(setActiveSite({ id: this.id }));
      }
    });
  }

  navigateSendEmail(): void {
    this.router.navigate(['admin', 'emails', 'send-email'], {
      queryParams: { siteId: this.id },
    });
  }

  onPaySite(): void {
    const sites = [this.site];
    const dialogRef = this.dialog.open(DialogConfirmPaymentComponent, {
      panelClass: 'fc-dialog-confirm-payment',
      data: {
        line1: '',
        periods: this.siteHelper.getPayablePaymentPeriods(sites),
        sites,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && !result.buttonTextIsNull && result.payPeriod !== null && result.payPeriod !== undefined) {
        this.paymentService
          .paySites([{ site_id: this.site.id, period: result.payPeriod }])
          .pipe(
            switchMap(response => {
              this.displayPaymentResults(response);
              return this.siteService.getSite(this.id);
            })
          )
          .subscribe(site => {
            this.site = site;
          });
      }
    });
  }

  displayPaymentResults(result: PaymentSitesResponse): void {
    let paymentText: string;

    if (result.hasError) {
      paymentText = 'There were errors processing the payments.';
    } else {
      paymentText = 'All payments completed successfully.';
    }

    this.dialog.open(DialogSitePaymentResultsComponent, {
      panelClass: 'fc-site-payments-dialog',
      data: {
        title: 'PAYMENT TRANSACTIONS',
        line1: paymentText,
        sitePayments: result.payments,
        buttonTextYes: 'OK',
      },
    });
  }

  canSplitSite() {
    return this.isSuperAdmin && this.site.activationDate !== null && this.site.canceledDate === null;
  }

  splitSite() {
    this.dialog
      .open(DialogSplitSiteComponent, { data: { site: this.site } })
      .afterClosed()
      .subscribe({
        next: (result: any) => {
          if (result) {
            this.dialog
              .open(DialogYesNoComponent, {
                panelClass: 'fc-yes-no-dialog',
                data: {
                  title: 'Warning',
                  line1: 'This process is irreversible! Please confirm that the site should be split.',
                  buttonTextYes: 'Yes, Split',
                  buttonTextNo: 'Cancel',
                },
                autoFocus: false,
              })
              .afterClosed()
              .subscribe({
                next: innerResult => {
                  if (innerResult) {
                    this.spinnerService.show('app-spinner', true, 'Splitting Site');
                    this.siteService.splitSite(this.site.id, result.effectiveDate, result.newKML).subscribe({
                      next: result => {
                        this.dialog
                          .open(DialogYesNoComponent, {
                            data: {
                              title: 'Site Split Successfully',
                              line1: 'The site was split successfully.',
                              buttonTextYes: 'Return to Dashboard',
                            },
                          })
                          .afterClosed()
                          .subscribe({
                            next: innerResult => {
                              this.redirectService.redirectToDashboard();
                            },
                          });
                      },
                      error: () => {
                        this.spinnerService.hide('app-spinner');
                      },
                      complete: () => {
                        this.spinnerService.hide('app-spinner');
                      },
                    });
                  }
                },
              });
          }
        },
      });
  }

  sendEdoc() {
    this.dialog
      .open(DialogSendEdocComponent, { data: { buyerId: this.site.cohort.buyerId } })
      .afterClosed()
      .subscribe({
        next: (result: any) => {
          if (result) {
            this.spinnerService.show('app-spinner', true, 'Sending Docusign Envelope');
            this.siteService.sendEdoc(this.site.id, result).subscribe(result => {
              this.spinnerService.hide('app-spinner');
              this.store.dispatch(setActiveSite({ id: this.id }));
            });
          }
        },
      });
  }

  siteAction($event: MatSelectChange) {
    switch ($event.value) {
      case 'status':
        this.onBulkChangeSiteState();
        break;
      case 'eDoc':
        this.sendEdoc();
        break;
      case 'email':
        this.navigateSendEmail();
        break;
      case 'pay':
        this.onPaySite();
        break;
      case 'split':
        this.splitSite();
        break;
      default:
        console.log('No Action found');
    }
    $event.source.value = '';
  }

  doOpenSite(): void {
    if (this.site.approvedDate == null && this.site.canceledDate == null) {
      this.dialog.open(DialogYesNoComponent, {
        data: {
          title: 'Warning',
          line1: 'By viewing a pending map, you are able to make changes to the landowners submission.',
          line2:
            'Be advised that any changes made will overwrite their submission and you will be required to send the site back to landowner for approval.',
          buttonTextYes: 'Continue',
        },
      });
    }
    this.store.dispatch(setMapControlDrawMode({ active: false }));
    this.mapHelper.navigateToMap(this.router, this.site, 'admin');
  }
}
